import { z } from 'zod';
import { ConversationSchema } from './conversation.js';
import { UserSchema, UserSettingsSchema } from './user.js';
import { BalanceSchema, CreditLotSchema, TokenUsageSchema, UsageByKindSchema, UserCreditsSchema } from './billing.js';
import { SkillStatusSchema } from './skill.js';
import { SubscriptionSchema } from './plan.js';
// What LLM calls consumed, summed across call kinds: the journal's tokens, and
// the credits they debited, in the stored sub-unit. Credits are absent, not
// zero, on a deployment that prices nothing.
export const AdminUsageSchema = TokenUsageSchema.extend({
    callCount: z.number().int().nonnegative(),
    credits: z.object({
        charged: z.number().int().nonnegative(),
        // Each debit divided by the multiplier that priced it. Absent as soon as one
        // debit recorded none: its multiplier is unknown, not one.
        atCost: z.number().int().nonnegative().optional()
    }).optional()
});
export const ConversationStatsSchema = z.object({
    messageCount: z.number().int().nonnegative(),
    artifactCount: z.number().int().nonnegative(),
    fileCount: z.number().int().nonnegative(),
    toolCallCount: z.number().int().nonnegative()
});
export const AdminConversationSchema = ConversationSchema.extend({
    userId: z.string(),
    user: UserSchema.optional(),
    usage: AdminUsageSchema.optional(),
    stats: ConversationStatsSchema.optional()
});
// --- Admin permissions ---
//
// The vocabulary shared by the API, which enforces it, and the admin front,
// which hides what the current key cannot do. One read/write pair per domain,
// deliberately coarse: a permission nobody can state in one line is a
// permission nobody grants correctly.
//
// `conversations:read` covers message content and user identity. Splitting the
// metadata from the content is defensible and can be added later; it cannot be
// un-split once keys are in partners' hands, so the coarse form is the one we
// commit to for now.
export const ADMIN_PERMISSIONS = [
    'conversations:read',
    'conversations:write',
    'billing:read',
    'billing:write',
    'catalog:read',
    'catalog:write',
    'skills:read',
    'skills:write',
    'keys:read',
    'keys:write',
    'users:read',
    // The plan catalogue. Distinct from `billing:write`, which moves one subject
    // between plans: editing a plan changes what every one of its subscribers
    // gets, and the two acts do not belong to the same hands.
    'plans:read',
    'plans:write',
    // Organizations and who belongs to them, with which role. Reading one shows
    // its members' identities, as the app knows them.
    'organizations:read',
    'organizations:write'
];
export const AdminPermissionSchema = z.enum(ADMIN_PERMISSIONS);
export function hasPermission(principal, permission) {
    return principal.kind === 'root' || principal.permissions.includes(permission);
}
/**
 * Which of these permissions the principal does not hold.
 *
 * Delegation only ever goes down: a key mints another key with permissions it
 * holds itself, never more. Without that rule `keys:write` is a universal
 * permission in disguise, and the split into domains means nothing.
 *
 * Returns what is missing rather than a boolean, so a refusal can name every
 * problem at once instead of one per round-trip.
 */
export function missingPermissions(principal, requested) {
    return requested.filter(permission => !hasPermission(principal, permission));
}
// An admin key on the wire (ISO date strings). Never carries the secret, which
// is readable once at creation, nor its hash, which never leaves the database.
export const AdminApiKeySummarySchema = z.object({
    id: z.string(),
    label: z.string(),
    permissions: z.array(AdminPermissionSchema),
    createdBy: z.string().optional(),
    createdAt: z.string(),
    expiresAt: z.string().optional(),
    revokedAt: z.string().optional(),
    lastUsedAt: z.string().optional()
});
// The answer to POST /admin/keys: the summary, plus the only moment the key
// itself is ever readable.
export const CreatedAdminApiKeySchema = AdminApiKeySummarySchema.extend({
    key: z.string()
});
// Body of POST /admin/keys. `expiresAt` is an absolute ISO-8601 instant with an
// offset, as everywhere else on this API; omitted means the key does not expire
// on its own.
export const CreateAdminApiKeyRequestSchema = z.object({
    label: z.string().trim().min(1),
    permissions: z.array(AdminPermissionSchema),
    expiresAt: z.string().optional()
});
// Body of PATCH /admin/keys/:keyId. Every field is optional and what is absent
// is left alone, so amending a label never restates a permission set.
//
// `expiresAt` is three-valued on purpose: an instant moves the expiry, `null`
// clears it, and omitting the field keeps it. A key that has lapsed is revived
// by moving its expiry — nothing else brings one back, and that is deliberate.
export const UpdateAdminApiKeyRequestSchema = z.object({
    label: z.string().trim().min(1).optional(),
    permissions: z.array(AdminPermissionSchema).optional(),
    expiresAt: z.string().nullable().optional()
});
// The answer to GET /admin/me: who is asking, and what this deployment can do.
//
// `permissions` is empty for the root principal, whose `kind` stands for all of
// them — read it through `hasPermission`, never by inspecting the list.
export const AdminMeSchema = z.object({
    subject: z.string(),
    kind: z.enum(['root', 'key']),
    // The key's label. Absent for the root principal, and absent from an older
    // API — the front falls back to the subject rather than showing nothing.
    label: z.string().optional(),
    permissions: z.array(AdminPermissionSchema),
    // Whether the deployment still has an environment key configured. Drives the
    // reminder to retire it.
    rootKeyConfigured: z.boolean(),
    // Whether this deployment can list its users at all — the app owns that table
    // and does not always have one. Drives whether the console offers the tab,
    // rather than offering it and answering 501.
    userListingAvailable: z.boolean().optional(),
    // Whether the app can find an account by its address, which is how the
    // console adds a member to an organization. Drives whether it offers to.
    memberLookupAvailable: z.boolean().optional()
});
// --- Users ---
//
// The user base does not live in core-api: each app owns its own `user` table
// and injects the reads. What core-api can say about a user is their traffic —
// conversations, tokens, credits — so a listed user is the app's identity with
// this deployment's activity hung off it.
export const AdminUserSchema = z.object({
    id: z.string(),
    // Identity comes from the app, which may know less about a user than it has
    // columns for — and may no longer know them at all, for an id that still
    // appears in this deployment's traffic.
    name: z.string().optional(),
    email: z.string().optional(),
    createdAt: z.string().optional(),
    conversationCount: z.number().int().nonnegative(),
    // Last time the user moved anything, not last time they signed in: core-api
    // sees conversations, not sessions.
    lastActivityAt: z.string().optional(),
    usage: AdminUsageSchema,
    // Credits are absent, not zero, on a deployment that prices nothing — a
    // column of structural zeros reads as "nobody consumed anything".
    balance: BalanceSchema.optional(),
    // The user's credits over the period in course, as their settings show them.
    // Also absent for a user on no plan: there is no period to count over.
    periodCredits: UserCreditsSchema.omit({ blocked: true }).optional(),
    // The subscription in force now, resolved server-side rather than picked out
    // of a history: choosing it takes a rule — the latest start covering the
    // instant — and a second implementation of that rule is a second chance to
    // get it wrong. Absent when the subject has none.
    currentSubscription: SubscriptionSchema.optional()
});
// One user in full: everything the listing shows, plus what only fits on a page
// of its own — the per-kind split of their calls, the lots their balance is made
// of, and the settings they chose.
//
// Their conversations are NOT here. That is conversation data, gated on
// `conversations:read`, and it pages; folding it in would either widen what
// `users:read` grants or cap the list at whatever fits one response.
export const AdminUserDetailSchema = AdminUserSchema.extend({
    settings: UserSettingsSchema,
    // The breakdown behind `usage`. `message` is the user's own turns; `title` and
    // `summary` are calls the platform makes on their behalf, and telling them
    // apart is the point of keeping the kind.
    usageByKind: z.array(UsageByKindSchema),
    // The envelopes the balance is computed from, newest expiry last. Absent for
    // the same reason `balance` is: a deployment that prices nothing has none.
    lots: z.array(CreditLotSchema).optional(),
    // Every subscription this subject ever had, newest first. What answers "and
    // before that?". Empty, never absent: a deployment that prices nothing still
    // has subscriptions, and an empty list says "none" where a missing field
    // would say "we did not look".
    subscriptions: z.array(SubscriptionSchema)
});
// One system skill as the console lists it. The switch a superadmin holds is
// `available`: the declaration on the `all` audience, which says whether the
// skill reaches every user by default. What each user then chose for
// themselves is theirs, and is not summarised here.
export const AdminSkillSchema = z.object({
    id: z.string(),
    // The technical identifier the agent sees, and what the CLI takes.
    slug: z.string(),
    // Human title, resolved for display. Falls back to the slug.
    title: z.string(),
    contentHash: z.string(),
    // Absent for a skill that has never been published.
    version: z.number().int().positive().optional(),
    available: z.boolean(),
    status: SkillStatusSchema.optional()
});
